// src/components/Toast/ToastProvider.jsx
// 🍞 File de toasts animés — charte Saniso (argousier #F9A825 / fiord #455A64).
// - 4 types : success (vert), error (rouge, 6 s), info (fiord), warning (argousier)
// - Entrée : glissement + fondu · Sortie : repli · Barre de progression
// - Max 4 à l'écran, auto-disparition, bouton fermer, aria-live
// - Respecte prefers-reduced-motion

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { registerToast } from './toast';

const DEFAULT_DURATION = { success: 2000, error: 3000, info: 4000, warning: 3000 };
const MAX_TOASTS = 4;

const STYLES = {
  success: {
    accent: '#059669',
    icon: FaCheckCircle,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
  },
  error: {
    accent: '#EF4444',
    icon: FaExclamationCircle,
    iconBg: 'bg-red-50',
    iconColor: 'text-red-500',
  },
  info: {
    accent: '#455A64', // fiord — couleur secondaire de la marque
    icon: FaInfoCircle,
    iconBg: 'bg-slate-100',
    iconColor: 'text-fiord',
  },
  warning: {
    accent: '#F9A825', // argousier — couleur primaire de la marque
    icon: FaExclamationTriangle,
    iconBg: 'bg-amber-50',
    iconColor: 'text-argousier',
  },
};

function ToastCard({ toast: t, onClose, onDone }) {
  const { accent, icon: Icon, iconBg, iconColor } = STYLES[t.type] || STYLES.info;
  const duration = t.duration || DEFAULT_DURATION[t.type] || 4000;
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(onDone, duration);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="toast-enter relative w-full max-w-sm bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden pointer-events-auto"
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className={`flex-shrink-0 w-9 h-9 rounded-full ${iconBg} flex items-center justify-center mt-0.5`}>
          <Icon className={`w-4.5 h-4.5 ${iconColor}`} />
        </div>
        <p className="flex-1 text-sm font-medium text-gray-800 leading-snug pt-1.5">{t.message}</p>
        <button
          onClick={onClose}
          aria-label="Fermer la notification"
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1 -m-1 rounded-md hover:bg-gray-100 transition-colors"
        >
          <FaTimes className="w-3.5 h-3.5" />
        </button>
      </div>
      <div
        className="toast-progress h-1"
        style={{
          background: `linear-gradient(90deg, ${accent}, #F9A825)`,
          animationDuration: `${duration}ms`,
        }}
      />
    </div>
  );
}

export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((t) => {
    setToasts((prev) => [...prev.slice(-(MAX_TOASTS - 1)), { id: Date.now() + Math.random(), ...t }]);
  }, []);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    registerToast(push);
    return () => registerToast(null);
  }, [push]);

  return (
    <>
      {children}
      <div
        aria-live="polite"
        className="fixed top-4 left-1/2 -translate-x-1/2 md:left-auto md:right-4 md:translate-x-0 z-[9999] flex flex-col gap-2.5 w-[calc(100%-2rem)] md:w-auto pointer-events-none"
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onClose={() => remove(t.id)} onDone={() => remove(t.id)} />
        ))}
      </div>
    </>
  );
}
